import { Card } from "@/components/ui/card";
import { ACCOUNT_STATUS_CONFIG } from "./StatusBadge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import type { TargetUniverseAccount, TargetUniverseStats, KanbanData } from "@/hooks/useTargetUniverse";

const TIER_ORDER = ["unreviewed", "cold", "validated_target", "nurture", "pipeline_ready", "customer", "disqualified", "parked"];

const TIER_BORDER_COLORS: Record<string, string> = {
  unreviewed: "border-l-gray-300",
  cold: "border-l-slate-400",
  validated_target: "border-l-blue-400",
  nurture: "border-l-purple-400",
  pipeline_ready: "border-l-green-400",
  customer: "border-l-emerald-400",
  disqualified: "border-l-red-400",
  parked: "border-l-gray-500",
};

interface TargetUniverseKanbanProps {
  kanbanData?: KanbanData;
  isLoading: boolean;
  onReview: (account: TargetUniverseAccount) => void;
  stats?: TargetUniverseStats | null;
  onLoadMore: (tier: string) => void;
}

export function TargetUniverseKanban({
  kanbanData,
  isLoading,
  onReview,
  stats,
  onLoadMore,
}: TargetUniverseKanbanProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Loading...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-2.5 p-4 min-w-max">
          {TIER_ORDER.map((tier) => {
            const config = ACCOUNT_STATUS_CONFIG[tier] || ACCOUNT_STATUS_CONFIG.cold;
            const tierData = kanbanData?.[tier];
            const items = tierData?.accounts || [];
            const totalCount = tierData?.totalCount ?? 0;
            const hasMore = tierData?.hasMore ?? false;

            return (
              <div key={tier} className="w-[240px] flex-shrink-0 flex flex-col rounded-lg border bg-muted/30">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <span className="text-sm font-semibold text-foreground">{config.label}</span>
                  <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                    {totalCount.toLocaleString()}
                  </span>
                </div>
                <ScrollArea className="flex-1 max-h-[calc(100vh-320px)]">
                  <div className="p-1.5 space-y-1">
                    {items.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">No accounts</p>
                    ) : (
                      items.map((acct) => (
                        <Card
                          key={acct.id}
                          className={`px-2.5 py-1.5 cursor-pointer hover:shadow-md transition-shadow border-l-4 ${TIER_BORDER_COLORS[tier] || "border-l-gray-400"}`}
                          onClick={() => onReview(acct)}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className="font-medium text-sm text-foreground truncate">{acct.name}</span>
                            {acct.contact_count > 0 && (
                              <span className="text-[10px] text-muted-foreground shrink-0">{acct.contact_count}c</span>
                            )}
                          </div>
                          {(acct.territory || acct.sales_rep) && (
                            <div className="text-[11px] text-muted-foreground truncate">
                              {[acct.territory, acct.sales_rep].filter(Boolean).join(" · ")}
                            </div>
                          )}
                        </Card>
                      ))
                    )}
                    {hasMore && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs h-7 text-muted-foreground"
                        onClick={() => onLoadMore(tier)}
                      >
                        <ChevronDown className="h-3 w-3 mr-1" />
                        Load more ({totalCount - items.length} remaining)
                      </Button>
                    )}
                  </div>
                </ScrollArea>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
